import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState, useEffect } from "react";
import { useMutation, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import {
  XMarkIcon,
  BuildingLibraryIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import EditAssignedVotersTab from "./EditAssignedVotersTab";
import { votersAPI } from "../../../services/api";
import { validatePrecinct } from "../../../validation/voters/precinct";

function EditPrecinctDetails({ isOpen, onClose, precinct }) {
  const [activeTab, setActiveTab] = useState("details");
  const queryClient = useQueryClient();
  const [assignedVoters, setAssignedVoters] = useState([]);
  const [loadingVoters, setLoadingVoters] = useState(false);

  // Default precinct structure
  const defaultPrecinct = {
    name: "",
    total_assigned_voters: "",
    location: "",
  };

  const [formData, setFormData] = useState(defaultPrecinct);
  const [errors, setErrors] = useState({});

  // Update form data when a precinct is passed in
  useEffect(() => {
    if (precinct) {
      setFormData({
        name: precinct.name || "",
        total_assigned_voters: precinct.total_assigned_voters || "",
        location: precinct.location || "",
      });
      setErrors({});
    } else {
      setFormData(defaultPrecinct);
      setErrors({});
    }
  }, [precinct]);

  useEffect(() => {
    const fetchAssigned = async () => {
      if (!precinct?.precinct_id || activeTab !== "voters") return;
      setLoadingVoters(true);
      try {
        const res = await votersAPI.getVotersByPrecinctId(precinct.precinct_id);
        const list =
          res?.data?.data?.voters_by_precinct ||
          res?.data?.data?.voters ||
          res?.data?.data ||
          [];
        setAssignedVoters(Array.isArray(list) ? list : []);
      } catch (e) {
        setAssignedVoters([]);
      } finally {
        setLoadingVoters(false);
      }
    };
    fetchAssigned();
  }, [precinct?.precinct_id, activeTab]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const updatePrecinctMutation = useMutation(
    ({ id, data }) => votersAPI.updatePrecinct(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("voters-precincts");
        toast.success("Precinct updated successfully");
        onClose();
      },
      onError: (error) => {
        console.error("Failed to update precinct", error);
        // Try to map backend validation errors if present
        const resp = error?.response?.data;
        if (resp?.errors && typeof resp.errors === "object") {
          const fieldErrors = {};
          Object.entries(resp.errors).forEach(([key, val]) => {
            if (Array.isArray(val) && val.length > 0) fieldErrors[key] = val[0];
            else if (typeof val === "string") fieldErrors[key] = val;
          });
          setErrors(fieldErrors);
        }
        const message = resp?.message || "Update failed. Please try again.";
        toast.error(message);
      },
    }
  );

  const handleUpdate = async () => {
    console.log("Updating precinct:", formData);
    // TODO: Call votersAPI.update(precinct.precinct_id, formData)
    if (!precinct?.precinct_id) {
      console.error("Missing precinct_id for update");
      return;
    }
    const result = await validatePrecinct(formData);
    if (!result.valid) {
      setErrors(result.errors || {});
      toast.error("Please fix the highlighted errors");
      return;
    }
    updatePrecinctMutation.mutate({ id: precinct.precinct_id, data: result.values });
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Overlay */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        {/* Modal Container */}
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-6 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-5xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
                  <div className="flex items-center gap-2">
                    {precinct?.name && (
                      <span className="px-3 py-1 bg-yellow-100 text-yellow-600 text-sm rounded-md">
                        {precinct.name}
                      </span>
                    )}
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-semibold text-gray-900"
                    >
                      Edit Precinct
                    </Dialog.Title>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 rounded-full hover:bg-gray-100 transition-transform transform hover:rotate-90 duration-200"
                  >
                    <XMarkIcon className="w-6 h-6 text-gray-500" />
                  </button>
                </div>

                {/* Tabs */}
                <div className="flex items-center border-b border-gray-200">
                  <button
                    onClick={() => setActiveTab("details")}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 font-medium ${
                      activeTab === "details"
                        ? "text-blue-600 border-b-2 border-blue-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <BuildingLibraryIcon className="w-5 h-5" />
                    Precinct Details
                  </button>
                  <button
                    onClick={() => setActiveTab("voters")}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 font-medium ${
                      activeTab === "voters"
                        ? "text-blue-600 border-b-2 border-blue-600"
                        : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    <UsersIcon className="w-5 h-5" />
                    Assigned Voters
                  </button>
                </div>

                {/* Content */}
                {activeTab === "details" && (
                  <div className="p-6">
                    <div className="border border-gray-300 rounded-2xl p-6 space-y-6">
                      {/* Precinct Name */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Precinct Name
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                        {errors.name && (
                          <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                        )}
                      </div>

                      {/* Total Assigned Voters */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Total Assigned Voters
                        </label>
                        <input
                          type="number"
                          name="total_assigned_voters"
                          value={formData.total_assigned_voters}
                          onChange={handleChange}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2"
                          disabled
                        />
                        {errors.total_assigned_voters && (
                          <p className="mt-1 text-sm text-red-600">{errors.total_assigned_voters}</p>
                        )}
                      </div>

                      {/* Location */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Location
                        </label>
                        <input
                          type="text"
                          name="location"
                          value={formData.location}
                          onChange={handleChange}
                          className="block w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                        {errors.location && (
                          <p className="mt-1 text-sm text-red-600">{errors.location}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Assigned Voters Tab */}
                {activeTab === "voters" && (
                  <EditAssignedVotersTab voters={assignedVoters} />
                )}

                {/* Footer */}
                <div className="border-t border-gray-200 mt-4 px-6 py-4 flex justify-end gap-2">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm"
                  >
                    Cancel
                  </button>
                  {activeTab !== "voters" && (
                    <button
                      onClick={handleUpdate}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm disabled:opacity-50"
                      disabled={updatePrecinctMutation.isLoading}
                    >
                      {updatePrecinctMutation.isLoading ? "Updating..." : "Update"}
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default EditPrecinctDetails;
